import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import LeadForm from "./LeadForm";

const LeadModalContext = createContext(() => {});

export const useLeadModal = () => useContext(LeadModalContext);

export function LeadModalProvider({ children }) {
  const [state, setState] = useState({ open: false, goal: "" });

  const openLead = useCallback((goal = "") => setState({ open: true, goal }), []);
  const close = useCallback(() => setState((s) => ({ ...s, open: false })), []);

  useEffect(() => {
    document.body.style.overflow = state.open ? "hidden" : "";
    const onKey = (e) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [state.open, close]);

  return (
    <LeadModalContext.Provider value={openLead}>
      {children}
      <AnimatePresence>
        {state.open && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-end justify-center p-0 sm:items-center sm:p-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="lead-modal-overlay"
          >
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={close} />
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 30, scale: 0.98 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="relative z-10 max-h-[92vh] w-full max-w-[560px] overflow-y-auto border border-[#c9a45c]/35 bg-[#0d0d0d] p-7 shadow-[0_40px_90px_rgba(0,0,0,0.7)] sm:p-9"
              role="dialog"
              aria-modal="true"
              data-testid="lead-modal"
            >
              <button
                onClick={close}
                aria-label="Chiudi"
                data-testid="lead-modal-close"
                className="absolute right-4 top-4 p-2 text-[#929292] transition-colors hover:text-gold-light"
              >
                <X size={18} />
              </button>
              <p className="eyebrow">Il punto di partenza</p>
              <h3 className="display mt-4 text-2xl sm:text-3xl" data-testid="lead-modal-title">
                Richiedi il tuo percorso.
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-[#929292]">
                Due minuti per raccontarci il tuo obiettivo: costruiamo il protocollo più adatto, con la regia
                di A.C. HUB.
              </p>
              <div className="mt-7">
                <LeadForm defaultGoal={state.goal} sourcePage="modal" onSubmitted={close} />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </LeadModalContext.Provider>
  );
}
